import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import HostedPaymentMock from '@/components/HostedPaymentMock';
import { aud, customerName } from '@/utils/format';
import { addDays, format, isAfter } from 'date-fns';
import {
  completeUniversalSelfBook,
  slotStartIso,
  startOptionsForDay,
  type SelfBookSession,
  type SelfBookStart,
} from '@/domain/selfBookFlow';
import { SESSION_DEPOSIT_AUD, SESSION_META } from '@/domain/pricing';
import { CheckCircle2, ImagePlus, X } from 'lucide-react';

type Stage = 'session' | 'slot' | 'details' | 'design' | 'pay' | 'done';

const SESSIONS: SelfBookSession[] = ['half-day', 'full-day'];

export default function SelfBookPage() {
  const { linkToken } = useParams<{ linkToken: string }>();
  const link = useBrainStore((s) => (linkToken ? s.selfBookLinks[linkToken] : undefined));
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // A pinned link (artist held a slot before the client opened it) skips the
  // session + slot stages entirely - the client only does details/design/pay.
  // An unpinned universal link keeps the full wizard, unchanged.
  const pinnedStart = link?.pinnedStartIso ? new Date(link.pinnedStartIso) : null;
  const pinnedSession = link?.pinnedStartIso ? (link.sessionLength as SelfBookSession | undefined) ?? null : null;
  const pinnedStartLabel = link?.pinnedStartIso
    ? (format(new Date(link.pinnedStartIso), 'HH:mm') as SelfBookStart)
    : null;

  const [stage, setStage] = useState<Stage>(pinnedStart ? 'details' : 'session');
  const [session, setSession] = useState<SelfBookSession | null>(pinnedSession);
  const [pickedDate, setPickedDate] = useState<Date | null>(pinnedStart);
  const [pickedStart, setPickedStart] = useState<SelfBookStart | null>(pinnedStartLabel);

  const linkedCustomer = link?.customerId ? customers[link.customerId] : undefined;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [designBrief, setDesignBrief] = useState('');
  const [referencePhotos, setReferencePhotos] = useState<string[]>([]);
  const [heldUntilIso, setHeldUntilIso] = useState<string | null>(null);

  // Prefill identity from a staff-sent named link; a universal link stays blank.
  useEffect(() => {
    if (linkedCustomer) {
      setName(customerName(linkedCustomer));
      setEmail(linkedCustomer.email ?? '');
      setMobile(linkedCustomer.phone);
    }
  }, [linkedCustomer]);

  useEffect(() => {
    if (stage === 'done' && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [stage]);

  const artist = link ? artists[link.artistId] : undefined;

  // Next fortnight of candidate days that offer at least one start for the chosen
  // session. Day-off, night-rule and paid-slot contention all fold into this.
  const dayOptions = useMemo(() => {
    if (!artist || !session) return [];
    return Array.from({ length: 14 }, (_, i) => addDays(new Date(clockIso), i + 2))
      .map((day) => ({ day, starts: startOptionsForDay(artist, day, session) }))
      .filter((d) => d.starts.length > 0);
  }, [artist, session, clockIso]);

  const startsForPicked = useMemo(() => {
    if (!artist || !session || !pickedDate) return [] as SelfBookStart[];
    return startOptionsForDay(artist, pickedDate, session);
  }, [artist, session, pickedDate]);

  if (!link) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This link has expired or was not generated yet. Get in touch with the studio.
        </p>
      </CustomerShell>
    );
  }

  const expired = isAfter(new Date(clockIso), new Date(link.expiresAt));
  if (expired) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This link has expired. Get in touch with the studio to book a new session.
        </p>
      </CustomerShell>
    );
  }

  const deposit = session ? SESSION_DEPOSIT_AUD[session] : 0;
  const detailsComplete = name.trim().length > 0 && email.trim().length > 0 && mobile.trim().length > 0;
  const designComplete = designBrief.trim().length > 0 && referencePhotos.length >= 1;

  // ---- DONE ----------------------------------------------------------------
  if (stage === 'done' && pickedDate && pickedStart) {
    return (
      <CustomerShell>
        <div className="text-center py-8" data-testid="self-book-success">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">You&apos;re booked</h1>
          <p className="font-serif text-cream-soft mt-2">
            See you {format(pickedDate, 'EEEE d MMMM')} at {pickedStart} with {artist?.displayName}.
          </p>
          {heldUntilIso ? (
            <p className="font-inter text-[11px] text-cream-soft mt-6 border border-gold-dim/30 rounded-md px-3 py-2">
              This slot is held for you until{' '}
              <span className="text-gold-bright">{format(new Date(heldUntilIso), 'EEE d MMM, h:mmaaa')}</span>{' '}
              (72 hours). A confirmation text is on its way.
            </p>
          ) : null}
        </div>
      </CustomerShell>
    );
  }

  // ---- PAY -----------------------------------------------------------------
  if (stage === 'pay' && session && pickedDate && pickedStart) {
    return (
      <CustomerShell>
        <h1 className="heading-display text-2xl text-cream">{aud(deposit)} deposit</h1>
        <p className="font-serif text-cream-soft mt-2">
          {name ? `${name.split(' ')[0]}, ` : ''}you&apos;re locking in {format(pickedDate, 'EEEE d MMMM')} at{' '}
          {pickedStart} with {artist?.displayName}.
        </p>
        <div className="mt-6">
          <HostedPaymentMock
            amountAud={deposit}
            description={`${SESSION_META[session].label} session with ${artist?.displayName}.`}
            testid="customer-hosted-payment"
            onConfirm={() => {
              const result = completeUniversalSelfBook({
                link,
                session,
                startIso: slotStartIso(pickedDate, pickedStart),
                details: { name, email, mobile },
                designBrief,
                referencePhotos,
              });
              setHeldUntilIso(result.heldUntilIso);
              setStage('done');
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => setStage('design')}
          className="font-mono text-xs text-cream-soft mt-4 underline"
        >
          Back to details
        </button>
      </CustomerShell>
    );
  }

  // ---- WIZARD (session → slot → details → design) --------------------------
  return (
    <CustomerShell>
      <header>
        {artist?.avatarUrl ? (
          <img
            src={artist.avatarUrl}
            alt={artist.displayName}
            className="h-16 w-16 rounded-full mx-auto mb-4 object-cover border border-gold-bright/60"
          />
        ) : null}
        <p className="label-small-caps text-gold-bright text-center">Book your session</p>
        <h1 className="heading-display text-3xl text-gold-bright text-center mt-1">
          with {artist?.displayName}
        </h1>
      </header>

      {/* 1 - SESSION (half / full only, no size) */}
      {stage === 'session' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Choose length</p>
          <div className="space-y-2">
            {SESSIONS.map((s) => {
              const selected = s === session;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`session-${s}`}
                  onClick={() => setSession(s)}
                  className={
                    selected
                      ? 'w-full flex items-center justify-between rounded-md border border-gold-bright bg-gold/10 px-4 py-3'
                      : 'w-full flex items-center justify-between rounded-md border border-gold-dim/30 px-4 py-3 hover:border-gold-bright/60'
                  }
                >
                  <span className="text-left">
                    <span className="font-inter text-sm text-cream block">{SESSION_META[s].label}</span>
                    <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                      {SESSION_META[s].description}
                    </span>
                  </span>
                  <span className="font-mono text-sm text-gold-bright">{aud(SESSION_DEPOSIT_AUD[s])} deposit</span>
                </button>
              );
            })}
          </div>
          <p className="font-serif text-cream-soft text-xs mt-3">
            No size to choose - {artist?.displayName} works that out with you on the day.
          </p>
          {session ? (
            <button
              type="button"
              onClick={() => setStage('slot')}
              className="btn-primary mt-6 w-full"
              data-testid="session-continue"
            >
              Continue
            </button>
          ) : null}
        </section>
      ) : null}

      {/* 2 - DAY + START (11:00 / 15:00 from real availability) */}
      {stage === 'slot' && session ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Pick a day</p>
          <div className="grid grid-cols-4 gap-2">
            {dayOptions.map(({ day }, i) => {
              const selected = pickedDate && day.toDateString() === pickedDate.toDateString();
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => {
                    setPickedDate(day);
                    setPickedStart(null);
                  }}
                  className={
                    selected
                      ? 'rounded-md border border-gold-bright bg-gold/10 px-2 py-2 text-cream'
                      : 'rounded-md border border-gold-dim/30 px-2 py-2 text-cream-soft hover:border-gold-bright/60'
                  }
                  data-testid={`pick-day-${i}`}
                >
                  <p className="font-inter text-[9px] uppercase tracking-widest">{format(day, 'EEE')}</p>
                  <p className="font-inter text-base mt-0.5">{format(day, 'd')}</p>
                  <p className="font-mono text-[9px] text-cream-soft">{format(day, 'MMM')}</p>
                </button>
              );
            })}
          </div>

          {pickedDate ? (
            <>
              <p className="label-small-caps text-gold-bright mb-3 mt-6">Pick a start</p>
              <div className="grid grid-cols-2 gap-2">
                {startsForPicked.map((start) => {
                  const selected = start === pickedStart;
                  return (
                    <button
                      key={start}
                      type="button"
                      onClick={() => setPickedStart(start)}
                      data-testid={`start-${start}`}
                      className={
                        selected
                          ? 'rounded-md border border-gold-bright bg-gold/10 px-2 py-2 font-mono text-sm text-cream'
                          : 'rounded-md border border-gold-dim/30 px-2 py-2 font-mono text-sm text-cream-soft hover:border-gold-bright/60'
                      }
                    >
                      {start}
                    </button>
                  );
                })}
              </div>
              <p className="font-serif text-cream-soft text-xs mt-3">
                {session === 'full-day'
                  ? 'A full day runs about eight hours, so start times are limited to what fits the artist’s day.'
                  : 'A half day runs about four hours.'}
              </p>
            </>
          ) : null}

          {pickedDate && pickedStart ? (
            <button
              type="button"
              onClick={() => setStage('details')}
              className="btn-primary mt-6 w-full"
              data-testid="slot-continue"
            >
              Continue
            </button>
          ) : null}
        </section>
      ) : null}

      {/* 3 - YOUR DETAILS (name / email / mobile) */}
      {stage === 'details' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Your details</p>
          {pinnedStart ? (
            <p className="font-serif text-cream-soft text-xs mb-3" data-testid="pinned-slot-summary">
              {artist?.displayName} has held {format(pinnedStart, 'EEEE d MMMM')} at {pinnedStartLabel}
              {' '}for you. Just add your details and pay.
            </p>
          ) : null}
          <div className="space-y-3">
            <Field label="Full name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="detail-name"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="detail-email"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
            <Field label="Mobile">
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                data-testid="detail-mobile"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
          </div>
          <p className="font-serif text-cream-soft text-xs mt-3">
            We use these to confirm your booking and send reminders.
          </p>
          <button
            type="button"
            disabled={!detailsComplete}
            onClick={() => setStage('design')}
            className="btn-primary mt-6 w-full disabled:opacity-50"
            data-testid="details-continue"
          >
            Continue
          </button>
        </section>
      ) : null}

      {/* 4 - DESIGN BRIEF (description + >=1 reference image, required) */}
      {stage === 'design' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Your idea</p>
          <textarea
            value={designBrief}
            onChange={(e) => setDesignBrief(e.target.value)}
            placeholder={`Describe the piece for ${artist?.displayName}…`}
            data-testid="design-brief"
            className="w-full min-h-[90px] bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          />
          <p className="label-small-caps text-gold-bright mt-4 mb-2">
            Reference images <span className="text-blood">· at least one</span>
          </p>
          <div className="flex gap-2 flex-wrap">
            {referencePhotos.map((src, i) => (
              <div
                key={src}
                className="relative h-16 w-16 rounded-md border border-gold-dim/40 bg-ink-dark grid place-items-center"
              >
                <span className="font-mono text-[9px] text-cream-soft">ref {i + 1}</span>
                <button
                  type="button"
                  onClick={() => setReferencePhotos((p) => p.filter((_, j) => j !== i))}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-ink-black border border-gold-dim/40 grid place-items-center"
                  aria-label={`Remove reference ${i + 1}`}
                >
                  <X size={11} className="text-cream-soft" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="add-reference"
              onClick={() =>
                setReferencePhotos((p) => [...p, `ref://upload-${p.length + 1}.jpg`])
              }
              className="h-16 w-16 rounded-md border border-dashed border-blood/70 grid place-items-center text-blood"
            >
              <ImagePlus size={18} />
            </button>
          </div>
          <button
            type="button"
            disabled={!designComplete}
            onClick={() => setStage('pay')}
            className="btn-primary mt-6 w-full disabled:opacity-50"
            data-testid="design-continue"
          >
            Continue to {aud(deposit)} deposit
          </button>
          {!designComplete ? (
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest text-center mt-3">
              Add a description and one reference to continue
            </p>
          ) : null}
        </section>
      ) : null}
    </CustomerShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">{label}</span>
      <span className="block mt-1">{children}</span>
    </label>
  );
}

export function CustomerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col items-center px-6 py-8">
      <div className="max-w-sm w-full">
        <p className="font-cinzel-deco text-xs text-gold-bright tracking-widest text-center mb-6">
          TATTOO VAULT
        </p>
        {children}
        <p className="font-inter text-[9px] text-cream-soft/70 text-center mt-10 uppercase tracking-widest">
          Test mode. Mock-everything. No real money moves.
        </p>
      </div>
    </div>
  );
}
